/**
 * DuplicateDocuments.js
 * Created by Ajit on 09/27/2021.
 */

import React, {useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import styled from 'styled-components';
import ModalControl from '../Modal/Modal';
import {labels} from '../../../util/utils';
import env from '../../../util/env';
import utilityFunction from '../../../util/utilityfunctions';
import {clearDuplicateDocuments, removeTaskDocument} from '../../../actions/Document/duplicatedocumentsactions';
import {overWriteDocumentWithDocumentHelper} from '../../../actions/Task/documenthubactions';
import DuplicateDocumentItem from './DuplicateDocumentItem';
import {raiseDochelperError} from '../../../actions/erroractions';
import {setIsModalOpen} from '../../../actions/setismodalopen';
import {documentTypes} from '../../../util/uiconstants';
import _ from 'lodash';

export default function DuplicateDocuments({
	parentName,
	className,
	relatedTasks = [],
	documentType,
	isFromDrawer,
	showCrashDocumentWarningMessage
}) {
	const dispatch = useDispatch();
	const duplicateDocuments = useSelector((state) => state.duplicateDocuments);

	const handleOkClick = () => {
		if (!isFromDrawer) {
			dispatch(setIsModalOpen(false));
		}

		let duplicates = _.cloneDeep(duplicateDocuments);
		// Overwrite Documents for selected Items
		const overWriteList = duplicates.filter((document) => document.isOverWrite);

		overWriteList.forEach((document) => {
			if (document.onOverwrite) {
				document.onOverwrite(document);
			} else {
				const query = {
					docUrl: env.getDocServiceUrl(),
					engagementId: document.engagementId,
					resourceUri: env.getURL('Engg_AuthAudience_URL'),
					docTypeId: document.docTypeId || document.documentTypeID,
					overwrite: true
				};

				const uploadUrl = `${env.getDocHelperConfig().docHelperApiUri}Document?${utilityFunction.createQueryString(
					query
				)}`;

				const promise = dispatch(
					overWriteDocumentWithDocumentHelper(
						uploadUrl,
						document.payload,
						{
							connectionid: window?.jQuery?.connection?.documentHubV2?.connection?.id
						},
						document.data ? document.data : document,
						relatedTasks,
						showCrashDocumentWarningMessage
					)
				);

				if (promise instanceof Promise) {
					promise.catch(() => {
						dispatch(raiseDochelperError(['0010', 'Connection Failed', labels.documentHelperConnectionIssue]));
					});
				}
			}
		});

		// Remove TaskDocument for Discarded Items
		const discardList = duplicates.filter((document) => !document.isOverWrite);

		discardList.forEach((document) => {
			// for doc list cancel, task document association will not be their.
			if (document?.payload?.taskId !== null) {
				dispatch(removeTaskDocument(document));
				document.isOverWrite = false;
				if (document.onOverwrite) {
					document.onOverwrite(document);
				}
			}
		});

		// clear duplicate document list
		dispatch(
			clearDuplicateDocuments([
				// For this we gather unique relatedEntityIds and parentNames
				...new Set(
					duplicates.map((item) => ({
						relatedEntityId: item.payload.relatedEntityId || item.payload.taskId,
						parentName: item.payload.parentName
					}))
				)
			])
		);
	};

	const handleCancelClick = () => {
		dispatch(setIsModalOpen(false));

		// Remove Task Document for each duplicate document
		duplicateDocuments.forEach((document) => {
			// for doc list cancel, task document association will not be their.
			if (document.payload.taskId !== null) {
				dispatch(removeTaskDocument(document));
			}
		});

		// clear duplicate document list
		dispatch(
			clearDuplicateDocuments([
				// For this we gather unique relatedEntityIds and parentNames
				...new Set(
					duplicateDocuments.forEach((item) => ({
						relatedEntityId: item.payload.relatedEntityId || item.payload.taskId,
						parentName: item.payload.parentName
					}))
				)
			])
		);
	};

	let duplicateDocumentList = null;

	if (duplicateDocuments?.length > 0) {
		duplicateDocumentList = duplicateDocuments.map((document) => {
			return <DuplicateDocumentItem parentName={parentName} key={document.documentId} document={document} />;
		});
	}

	useEffect(() => {
		if (duplicateDocuments?.length > 0) {
			dispatch(setIsModalOpen(true));
		}
	}, [dispatch, duplicateDocuments]);

	return (
		duplicateDocuments?.length > 0 && (
			<ModalControl
				title={labels.uploadDocumentLabel}
				onOkClick={() => handleOkClick()}
				onHide={() => handleCancelClick()}
				show={duplicateDocuments?.length > 0}
				modalsize="small"
				showButtons
				hideCancelButton
				closeBtnTitle={labels.cancelLabel}
				confirmBtnTitle={labels.confirm}
				closeTitle={labels.closeLabel}
			>
				<StyledDuplicateDocumentContent className={`StyledDuplicateDocumentContent ${className}`}>
					<h4>
						{labels.duplicateDocumentHeader}
						<span>{labels.duplicateDocumentInstruction}</span>
					</h4>
					<ul>{duplicateDocumentList}</ul>
				</StyledDuplicateDocumentContent>
			</ModalControl>
		)
	);
}

const StyledDuplicateDocumentContent = styled.section`
	h4 {
		font-size: var(--px-16);
		margin: 0;
		padding: 0 0 var(--px-20) 0;
		font-weight: bold;
		span {
			font-weight: normal;
			display: block;
			padding-top: var(--px-5);
		}
	}
	ul {
		margin: 0 0 var(--px-20);
		max-height: var(--px-300);
		overflow-y: auto;
		padding: 0;
		li {
			padding: var(--px-20) 0 0 0;
		}
	}
`;
